import { Component, Inject, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControl, FormGroup } from '@angular/forms';
import { TaskService } from '@services/tasks/task.service';
import { NotificationServiceUP } from '@services/notifications/notification-service-u-p.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    NotificationResponse,
    TaskResponse,
    TaskSearchRequest,
    UserResponse,
} from '@core/generated/openapi';
import { AuthService } from '@services/auth/auth.service';
import moment from 'moment/moment';
import PriorityListEnum = TaskSearchRequest.PriorityListEnum;
import { AccountUsersService } from '@services/account-users/account-users.service';
import { UserNotificationsService } from '@services/user-notifications/user-notifications.service';

import type { Option } from '@rcp/common-web';
import { ButtonStyle, ButtonType } from '@rcp/common-web';
import type { ButtonDetailClassic } from '@rcp/common-web';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@core/material/material.module';
import { COMMON_WEB_IMPORTS } from '@core/modules/common-web-imports';
import { PipesModule } from '@core/pipes/pipes.module';
import { CommonInputsModule } from '@modules/common/modules/inputs/common-inputs/common-inputs.module';
@UntilDestroy()
@Component({
    selector: 'rcp-oup-todo-from-notification-dialog',
    templateUrl: './todo-from-notification-dialog.component.html',
    styleUrls: ['./todo-from-notification-dialog.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MaterialModule,
        COMMON_WEB_IMPORTS,
        PipesModule,
        CommonInputsModule,
    ],
    standalone: true,
})
export class TodoFromNotificationDialogComponent implements OnInit {
    notification: NotificationResponse;
    currentUser: UserResponse;

    toDoForm: FormGroup;
    notificationForm: FormGroup;
    nextStep: number = 1;
    accountUsers: Option[] = [];
    TODAY: Date = moment().startOf('day').toDate();
    PRIORITIES = TaskResponse.PriorityEnum;
    isSubmitInProcess: boolean = false;

    priorityOptions: Option[] = [
        {
            value: PriorityListEnum.Normal,
            label: 'Normal',
        },
        {
            value: PriorityListEnum.Important,
            label: 'High',
        },
    ];

    constructor(
        private taskService: TaskService,
        private notificationService: UserNotificationsService,
        private alertService: NotificationServiceUP,
        private authService: AuthService,
        private accountUserService: AccountUsersService,
        private dialogRef: MatDialogRef<TodoFromNotificationDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {
        this.authService
            .getCurrentUser()
            .pipe(untilDestroyed(this))
            .subscribe((user) => {
                this.currentUser = user;
            });
    }

    ngOnInit(): void {
        this.notification = this.data.notification;
        this.accountUserService
            .findAllAccountUsers()
            .pipe(untilDestroyed(this))
            .subscribe((users) => {
                let activeUsers = users.filter(
                    (user) => user.status == 'ACTIVE',
                );
                activeUsers.sort((a, b) => {
                    const nameA = a.lastName + ', ' + a.firstName;
                    const nameB = b.lastName + ', ' + b.firstName;
                    return nameA.localeCompare(nameB);
                });
                activeUsers.map((user) => {
                    this.accountUsers.push({
                        value: user.id,
                        label: user.lastName + ', ' + user.firstName,
                    });
                });
            });
        this.loadForm();
    }

    loadForm() {
        this.toDoForm = this.taskService.getCreateTaskForm(this.currentUser);
        this.toDoForm.addControl(
            'notificationId',
            new FormControl(this.notification.id),
        );
        this.toDoForm.addControl(
            'worklistModule',
            new FormControl(this.notification.worklistModule),
        );
        this.toDoForm
            .get('worklistModule')
            ?.setValue(this.notification.worklistModule);

        this.notificationForm =
            this.notificationService.getEditNotificationForm(this.notification);
    }

    submit() {
        this.isSubmitInProcess = true;
        this.taskService
            .createTask(this.toDoForm)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (response) => {
                    if (this.nextStep === 1) {
                        this.alertService.showSuccessMessage(
                            'To-Do was successfully created!',
                            '',
                        );
                        this.close(response);
                    }
                },
                error: (error) => {
                    this.alertService.showError(
                        'Failed to create To-Do',
                        error.error,
                    );
                },
            });
        if (this.nextStep === 2) {
            this.notificationService
                .clearNotification(this.notificationForm)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (result) => {
                        this.alertService.showSuccessMessage(
                            'Notification status was updated to ‘Cleared’ and a To-Do was created!',
                            '',
                        );
                        this.close(result);
                    },
                    error: (error) => {
                        this.alertService.showError(
                            'Failed to clear Notification',
                            error.error,
                        );
                    },
                });
        }
        this.isSubmitInProcess = false;
    }

    close(result?: any) {
        this.dialogRef.close(result);
    }

    protected readonly ButtonStyle = ButtonStyle;
    protected readonly ButtonType = ButtonType;
}
